import { Component, OnInit, OnDestroy, EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { Subscription } from 'rxjs';
import { BLACKSMITHING_MODULES } from './blacksmithing.data';
import { BlacksmithingModule, ModuleProgress, UserProgress, QuizQuestion } from './blacksmithing.model';

type AppView = 'overview' | 'module' | 'quiz';

@Component({
    selector: 'app-blacksmithing',
    templateUrl: './blacksmithing.component.html',
    styleUrls: ['./blacksmithing.component.scss'],
    standalone: false
})
export class BlacksmithingComponent implements OnInit, OnDestroy {
  private injector = inject(EnvironmentInjector);
  modules = BLACKSMITHING_MODULES;
  currentView: AppView = 'overview';
  selectedModule: BlacksmithingModule | null = null;

  userProgress: UserProgress = {};
  userId: string | null = null;

  // Quiz state
  currentQuestionIndex = 0;
  selectedAnswerIndex: number | null = null;
  showExplanation = false;
  quizAnswers: number[] = [];
  quizCompleted = false;
  quizScore = 0;

  // Video modal
  activeVideoId: string | null = null;
  activeVideoTitle = '';

  private authSub?: Subscription;
  private progressSub?: Subscription;

  constructor(
    private auth: AngularFireAuth,
    private db: AngularFireDatabase
  ) {}

  private inCtx<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  ngOnInit() {
    this.authSub = this.auth.user.subscribe(user => {
      this.userId = user?.uid ?? null;
      if (this.userId) {
        this.loadProgress();
      } else {
        this.userProgress = {};
      }
    });
  }

  ngOnDestroy() {
    this.authSub?.unsubscribe();
    this.progressSub?.unsubscribe();
  }

  private loadProgress() {
    if (!this.userId) return;
    this.progressSub?.unsubscribe();
    this.progressSub = this.inCtx(() => this.db
      .object<UserProgress>(`blacksmithing-progress/${this.userId}`)
      .valueChanges())
      .subscribe(progress => {
        this.userProgress = progress || {};
      });
  }

  get mainModules(): BlacksmithingModule[] {
    return this.modules.filter(m => !m.isBranchPath);
  }

  get branchModules(): BlacksmithingModule[] {
    return this.modules.filter(m => m.isBranchPath);
  }

  get overallProgress(): number {
    const main = this.mainModules;
    if (!main.length) return 0;
    let points = 0;
    const maxPoints = main.length * 2;
    main.forEach(m => {
      const p = this.userProgress[m.id];
      if (p?.visited) points++;
      if (p?.quizCompleted) points++;
    });
    return Math.round((points / maxPoints) * 100);
  }

  getModuleProgress(moduleId: string): ModuleProgress | null {
    return this.userProgress[moduleId] || null;
  }

  isModuleUnlocked(module: BlacksmithingModule): boolean {
    if (!module.unlockAfterModule) return true;
    return !!this.userProgress[module.unlockAfterModule]?.visited;
  }

  getModuleStatus(module: BlacksmithingModule): 'locked' | 'available' | 'visited' | 'completed' {
    if (!this.isModuleUnlocked(module)) return 'locked';
    const p = this.userProgress[module.id];
    if (!p?.visited) return 'available';
    if (p?.quizCompleted) return 'completed';
    return 'visited';
  }

  openModule(module: BlacksmithingModule) {
    if (!this.isModuleUnlocked(module)) return;
    this.selectedModule = module;
    this.currentView = 'module';
    this.activeVideoId = null;
    this.resetQuiz();
    this.markVisited(module.id);
    window.scrollTo(0, 0);
  }

  private markVisited(moduleId: string) {
    if (!this.userId) return;
    const existing = this.userProgress[moduleId];
    if (existing?.visited) return;
    this.inCtx(() => this.db.object(`blacksmithing-progress/${this.userId}/${moduleId}`).update({
      visited: true,
      lastVisited: Date.now()
    }));
  }

  goToOverview() {
    this.currentView = 'overview';
    this.selectedModule = null;
    this.activeVideoId = null;
    this.resetQuiz();
    window.scrollTo(0, 0);
  }

  backToModule() {
    this.currentView = 'module';
    this.resetQuiz();
    window.scrollTo(0, 0);
  }

  startQuiz() {
    if (!this.selectedModule?.quiz?.length) return;
    this.currentView = 'quiz';
    this.resetQuiz();
    window.scrollTo(0, 0);
  }

  private resetQuiz() {
    this.currentQuestionIndex = 0;
    this.selectedAnswerIndex = null;
    this.showExplanation = false;
    this.quizAnswers = [];
    this.quizCompleted = false;
    this.quizScore = 0;
  }

  selectAnswer(index: number) {
    if (this.showExplanation) return;
    this.selectedAnswerIndex = index;
  }

  submitAnswer() {
    if (this.selectedAnswerIndex === null) return;
    this.quizAnswers.push(this.selectedAnswerIndex);
    this.showExplanation = true;
  }

  nextQuestion() {
    if (!this.selectedModule) return;
    if (this.currentQuestionIndex < this.selectedModule.quiz.length - 1) {
      this.currentQuestionIndex++;
      this.selectedAnswerIndex = null;
      this.showExplanation = false;
    } else {
      this.finishQuiz();
    }
  }

  private finishQuiz() {
    if (!this.selectedModule) return;
    const questions = this.selectedModule.quiz;
    const correct = questions.filter((q, i) => this.quizAnswers[i] === q.correctIndex).length;
    this.quizScore = Math.round((correct / questions.length) * 100);
    this.quizCompleted = true;

    if (this.userId) {
      const existing = this.userProgress[this.selectedModule.id];
      const shouldSave = !existing?.quizCompleted || this.quizScore > (existing?.quizScore ?? 0);
      if (shouldSave) {
        this.inCtx(() => this.db.object(`blacksmithing-progress/${this.userId}/${this.selectedModule.id}`).update({
          quizCompleted: true,
          quizScore: this.quizScore,
          visited: true,
          lastVisited: Date.now()
        }));
      }
    }
  }

  get currentQuestion(): QuizQuestion | null {
    if (!this.selectedModule) return null;
    return this.selectedModule.quiz[this.currentQuestionIndex] || null;
  }

  get isCurrentAnswerCorrect(): boolean {
    if (this.selectedAnswerIndex === null || !this.currentQuestion) return false;
    return this.selectedAnswerIndex === this.currentQuestion.correctIndex;
  }

  isAnswerCorrectForIndex(questionIndex: number): boolean {
    if (!this.selectedModule) return false;
    return this.quizAnswers[questionIndex] === this.selectedModule.quiz[questionIndex].correctIndex;
  }

  getScoreMessage(score: number): string {
    if (score >= 90) return 'NACHO! NACHO! NACHO! You have conquered this quiz, and it was the greatest quiz in the world.';
    if (score >= 70) return '"I am the gatekeeper of my own destiny and I will have my destiny!" Keep forging forward.';
    return '"Those eggs were a lie, Steven." Head back to the lesson and try again.';
  }

  getScoreClass(score: number): string {
    if (score >= 90) return 'score-gold';
    if (score >= 70) return 'score-pass';
    return 'score-retry';
  }

  openVideo(youtubeId: string | undefined, title: string, searchQuery: string) {
    if (youtubeId) {
      this.activeVideoId = youtubeId;
      this.activeVideoTitle = title;
    } else {
      const url = `https://www.youtube.com/results?search_query=${encodeURIComponent(searchQuery)}`;
      window.open(url, '_blank');
    }
  }

  closeVideo() {
    this.activeVideoId = null;
    this.activeVideoTitle = '';
  }

  getYouTubeUrl(searchQuery: string): string {
    return `https://www.youtube.com/results?search_query=${encodeURIComponent(searchQuery)}`;
  }
}
